import { GppGoodRounded } from '@mui/icons-material'
import {
  Button,
  Link,
  Stack,
  Typography,
  styled,
} from '@mui/material'
import Image from 'components/Image'
import { StyledBaseContainer } from '~/commons/styled'
import { TERMS_CONDITION_URL } from '~/config'
import { useBoundVerificationUserStore } from '../../stores'
import { useState } from 'react'
import { PhoneVerificationModal } from '../PhoneVerificationModal'
import useAuth from 'hooks/useAuth'
import useSession from 'hooks/commons/useSession'
import trackEvent from '~/trackers'
import { useRouter } from 'next/router'
import useLocales from 'hooks/useLocales'

const StyledMenuContainer = styled(Stack)(({ theme }) => ({
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  padding: '17px 12px',
  [theme.breakpoints.up('md')]: {
    padding: '17px 48px',
  },
  textAlign: 'center',
  alignItems: 'center',
}))

export default function VerificationDashboard() {
  const { t } = useLocales()
  const { pathname, query } = useRouter()
  const { user } = useAuth()
  const { canPerform } = useSession()
  const { setActiveStep } = useBoundVerificationUserStore(
    (state) => ({
      setActiveStep: state.setActiveStep,
    }),
  )

  const trackerData = { pathname, query, user }

  const [
    isPhoneVerificationDialogVisible,
    setIsPhoneVerificationDialogVisible,
  ] = useState(false)

  return (
    <StyledBaseContainer
      maxWidth={'712px'}
      paddingX={'40px'}
      margin={'0px auto'}
    >
      <Stack spacing={1}>
        <Typography variant="h4">
          {t('key_verify_your_data')}
        </Typography>
        <Typography variant="body2">
          {t('key_complete_your_data_to')}
        </Typography>
      </Stack>
      <Stack
        marginTop={3}
        display={'flex'}
        spacing={3}
        direction={'column'}
      >
        <Stack direction={'row'} spacing={{ xs: '12px', md: 3 }}>
          <StyledMenuContainer width={'50%'} spacing={1}>
            <Image
              src="/assets/icons/kyc/partnership.svg"
              alt="sponsorship-icon"
              sx={{ width: '48px', height: '48px' }}
            />
            <Typography variant="body2">
              {t('key_opportunity_to_get')}
            </Typography>
          </StyledMenuContainer>

          <StyledMenuContainer width={'50%'} spacing={1}>
            <Image
              src="/assets/icons/kyc/brand.svg"
              alt="brand-icon"
              sx={{ width: '48px', height: '48px' }}
            />
            <Typography variant="body2">
              {t('key_opportunity_to_collaborate')}
            </Typography>
          </StyledMenuContainer>
        </Stack>

        {/* 100%, not 50% like the row above: this benefit has no pair. */}
        <Stack direction={'row'} spacing={{ xs: '12px', md: 3 }}>
          <StyledMenuContainer width={'100%'} spacing={1}>
            <Image
              src="/assets/icons/kyc/income.svg"
              alt="income-icon"
              sx={{ width: '48px', height: '48px' }}
            />
            <Typography variant="body2">
              {t('key_making_disbursement')}
            </Typography>
          </StyledMenuContainer>
        </Stack>

        <Stack
          direction={'row'}
          spacing={'12px'}
          alignItems={'center'}
          justifyContent={'center'}
        >
          <GppGoodRounded color="primary" />
          <Typography variant="body2" color={'primary'}>
            {t('key_tiptip_guarantees')}
          </Typography>
        </Stack>
        <Typography
          display={{ md: 'inline-flex' }}
          alignItems={'center'}
          variant="body2"
        >
          {t('key_by_filling_in_this_data')}
          <Link
            id="l-users-verificationdashboard-terms"
            margin={{ md: '0px 4px' }}
            sx={(theme) => ({
              color: theme.palette.info.main,
              cursor: 'pointer',
            })}
            variant="body2"
            onClick={() => {
              trackEvent.user('click_tnc', trackerData)
              window.open(TERMS_CONDITION_URL, '_blank')
            }}
          >
            {' '}
            {t('key_terms_and_conditions')}{' '}
          </Link>
          TipTip
        </Typography>

        {/* Owner-only and non-overridable: KYC documents are the creator's
            personal legal identity, so no member may start the process. */}
        {canPerform('kyc:create') && (
          <Button
            id="b-users-verificationdashboard-start-verification"
            variant="contained"
            size="large"
            onClick={() => {
              trackEvent.user('click_verify_kyc', trackerData)
              if (!user?.phone) {
                setIsPhoneVerificationDialogVisible(true)
              } else {
                setActiveStep(0)
              }
            }}
          >
            {t('key_start_verification')}
          </Button>
        )}
      </Stack>

      <PhoneVerificationModal
        openDialog={setIsPhoneVerificationDialogVisible}
        open={isPhoneVerificationDialogVisible}
      />
    </StyledBaseContainer>
  )
}
